import * as React from 'react';
import { useState } from 'react';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { Link } from '~shared/components/Link';
import { toast } from '~shared/components/Toast';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import {
  FileDrop,
  FileDropUploadState,
} from '~teacher/components/FileDrop/FileDrop';
import {
  createNoOpHandler,
  createUploadToS3Handler,
} from '~teacher/components/FileDrop/FileHandlers';
import { FileHandlerConfigBuilder } from '~teacher/components/FileDrop/FileHandlers/FileHandlerConfig';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { LearnMoreIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/LearnMoreIcon';
import { proCheck } from '~teacher/util/proCheck';

import { DispatchExtractQuestionsAction } from './useExtractQuestionsReducer';

const MAX_FILES = 1;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

interface ExtractQuestionsFormProps {
  dispatch: DispatchExtractQuestionsAction;
  includeExplanation: boolean;
  onExtract: () => void;
}

export function ExtractQuestionsForm({
  onExtract,
  includeExplanation,
  dispatch,
}: ExtractQuestionsFormProps) {
  const [uploadingState, setUploadingState] =
    useState<FileDropUploadState>('idle');
  const fileMetadataRef = React.useRef<{ type: string; size: number } | null>(
    null
  );

  const isPro = proCheck.isPro();

  const handler = React.useMemo(() => {
    const onSuccess = ({ fileKey }: { fileKey: string }) => {
      const metadata = fileMetadataRef.current;

      dispatch({
        type: 'addFileKey',
        payload: {
          newFileKey: fileKey,
          fileType: metadata?.type || '',
          fileSize: metadata?.size || 0,
        },
      });
    };

    const onError = () => {
      toast.critical(translate('Required field(s) missing'));
    };

    const baseHandler = isPro
      ? createUploadToS3Handler({ onSuccess, onError })
      : createNoOpHandler({ onSuccess, onError });

    /* Capture metadata for BOTH handlers (pro + free)
     ** Analytics stay consistent
     */
    return async ({
      file,
      onUploadProgress,
    }: {
      file: File;
      onUploadProgress: (progress: ProgressEvent) => void;
    }) => {
      fileMetadataRef.current = { type: file.type, size: file.size };
      return baseHandler({ file, onUploadProgress });
    };
  }, [dispatch, isPro]);

  const fileHandlerConfig = new FileHandlerConfigBuilder()
    .addHandlerForGroups(handler, [
      { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
      { extensions: ['csv'], mimeTypes: ['text/csv'] },
      { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'] },
      { extensions: ['png'], mimeTypes: ['image/png'] },
    ])
    .build();

  return (
    <>
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex items-center gap-2">
              <Link
                indicatorProps={{ isInset: false, notOffset: true }}
                href={
                  'https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </Link>
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700 flex items-center gap-2 mt-2">
              {translate('Extract Questions from Document')}
              {proCheck.hasPaidPlan() ? (
                <Badge textTransform="uppercase" tone="beta">
                  {translate('Beta')}
                </Badge>
              ) : (
                <Badge className="font-sans" tone="pro">
                  Pro
                </Badge>
              )}
            </div>
            <div className="text-sans text-base leading-normal text-blue-900 mt-1">
              {translate(
                "Upload a file, and we'll find and extract the questions in it."
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="flex flex-col gap-4 pt-5"
        style={{
          boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
        }}
      >
        <FileDrop
          label={<FileDrop.Label label={translate('Upload Document')} />}
          onUploadStateChange={setUploadingState}
          onFileRemove={() => {
            dispatch({ type: 'removeFileKey' });
          }}
          maxFiles={MAX_FILES}
          maxFileSize={MAX_FILE_SIZE}
          {...fileHandlerConfig}
        />
      </div>
      <div className="flex justify-between items-center gap-2 py-5 pr-5 shadow-inset-border-t">
        <div className="flex flex-row items-center gap-2">
          <label
            className="text-base text-blue-900"
            htmlFor="generate-explanations-toggle"
            id="generate-explanations-label"
          >
            {translate('Generate explanations')}{' '}
          </label>
          <TooltipV2
            mode="click"
            contentWidth={260}
            label={translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          >
            <InformationIcon />
          </TooltipV2>
          <span id="generate-explanations-description" className="sr-only">
            {translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          </span>
        </div>
        <ToggleSwitch
          id="generate-explanations-toggle"
          labelledBy="generate-explanations-label"
          describedBy="generate-explanations-description"
          size="small"
          on={includeExplanation}
          onChange={(value) => {
            dispatch({
              type: 'setIncludeExplanation',
              payload: { newIncludeExplanation: value },
            });
          }}
        />
      </div>
      <div className="flex justify-end pt-5 shadow-inset-border-t">
        <SmallButton
          onClick={onExtract}
          isDisabled={uploadingState !== 'idle'}
          appearance="primary"
        >
          {translate('Add')}
        </SmallButton>
      </div>
    </>
  );
}
